// Client-safe guard for the CCSD/BUPA code search autocomplete behaviour.
//
// Client-safe guard for the CCSD/BUPA code search autocomplete behaviour.
//
// This module is imported from both the server bundle (SSR / search services)
// and client components, so it must stay free of `server-only` imports. The
// runtime override is read through the same-origin `/api/feature-flags` proxy
// (runtimeFlagsClient); on the server the loader never runs in browser code.
//
// Registry default: the env-scoped registry (src/lib/featureFlags.ts) is the
// offline source of truth. The runtime flag map published by Manifest
// overrides it without a redeploy when the key is present (dashboard toggle
// wins); missing or unreachable flags fall back to the registry.
//
// Environment resolution is consistent across hydration:
//   - server: APP_ENV (reliable per-deploy runtime value)
//   - client: request hostname (works even when staging and production share
//     the same build artifact)

import { getAppEnvironment, type AppEnvironment } from '@/lib/appEnvironment';
import {
  CCSD_SEARCH_FLAG_KEY,
  isFeatureFlagEnabledIn,
} from '@/lib/featureFlags';
import { isRuntimeFlagEnabled } from '@/lib/runtimeFlagsClient';

/** Flag key for the CCSD code search behaviour (single source of truth). */
export { CCSD_SEARCH_FLAG_KEY };

function getClientHostname(): string {
  if (typeof window === 'undefined') return '';
  const hostname =
    window.location && typeof window.location.hostname === 'string'
      ? window.location.hostname
      : '';
  return hostname;
}

/**
 * Environment for the CCSD gate.
 * - server (no window): APP_ENV.
 * - client: hostname → production for `*.lips.org.uk` without "staging",
 *   staging for `*.staging.lips.org.uk` and all other non-production hosts.
 */
function resolveCcsdEnvironment(): AppEnvironment {
  const hostname = getClientHostname();
  if (!hostname) return getAppEnvironment();

  const host = hostname.toLowerCase();
  if (host.includes('staging')) return 'staging';
  if (host.endsWith('lips.org.uk') && !host.includes('staging')) {
    return 'production';
  }
  return 'staging';
}

/** Registry default: true where the env-scoped registry opts this flag in. */
export function getCcsdSearchRegistryDefault(
  environment: AppEnvironment = resolveCcsdEnvironment(),
): boolean {
  return isFeatureFlagEnabledIn(CCSD_SEARCH_FLAG_KEY, environment);
}

/**
 * True when the current runtime is the PRODUCTION site. Production is the
 * `*.lips.org.uk` host WITHOUT "staging"; localhost and any
 * `*.staging.lips.org.uk` host are NOT production.
 */
export function isCcsdProductionEnvironment(): boolean {
  return resolveCcsdEnvironment() === 'production';
}

/**
 * Runtime-aware gate for shared modules (search services run server-side on
 * SSR and client-side in the browser). Client-safe: reads the same-origin
 * `/api/feature-flags` proxy when available with the env-scoped registry as
 * the fallback, so dashboard toggles apply per environment without a redeploy.
 */
export async function isCcsdSearchEnvironmentAllowed(): Promise<boolean> {
  return isCcsdSearchEnabledClient();
}

/**
 * Client-side flag value. Registry default first (synchronous, no flash),
 * runtime override applied by consumers once `/api/feature-flags` resolves.
 */
export async function isCcsdSearchEnabledClient(): Promise<boolean> {
  return isRuntimeFlagEnabled(
    CCSD_SEARCH_FLAG_KEY,
    getCcsdSearchRegistryDefault(),
  );
}
